/**
 * In-Memory Source of Truth for Document Properties
 * 
 * Based on RFC: partial-delta-sync.md
 * 
 * This class maintains all object properties in memory and handles:
 * - Property storage with replica_id + timestamp keys for O(1) access
 * - Object property tracking per object
 * - Pending persistence queue
 * - Merkle tree and version vector computation
 * - Memory management and cleanup
 */

import { computeContentHash, computeSubtreeHash, PropertyForHashing } from '@object-property-lww/database-utils';

export interface Property {
  objectId: string;
  propertyName: string;
  value: any;
  lwwCounter: number | null; // NULL for special properties (object.*)
  lwwRandom: number | null;  // NULL for special properties (object.*)
  replicaId: number;
  logicalTimestamp: number;
  source: 'local' | 'remote' | 'user';
  addedToMemoryAt: number; // timestamp when added to memory
}

export interface ObjectMetadata {
  objectId: string;
  objectType: string;
  parentId: string | null;
  propertyNames: Set<string>; // Set of property names for this object
  contentHash?: string;
  subtreeHash?: string;
  versionVector?: Record<string, number>;
  lastHashComputation?: number;
}

export interface PendingPersistence {
  properties: Property[];
  addedAt: number;
}

export class InMemorySourceOfTruth {
  // Core storage: object_id:property_name -> Property (LWW conflicts resolved before storage)
  private propertyMap = new Map<string, Property>();
  
  // Object tracking: object_id -> ObjectMetadata
  private objectMap = new Map<string, ObjectMetadata>();
  
  // Pending persistence queue
  private pendingPersistence: PendingPersistence[] = [];
  
  constructor(documentId: string) {
    // documentId parameter kept for API compatibility but not stored
  }

  /**
   * Add a property to in-memory storage
   * This is the main entry point for all property additions
   * Resolves LWW conflicts before storing
   */
  addProperty(property: Property): void {
    const key = this.makePropertyKey(property);
    const existingProperty = this.propertyMap.get(key);
    
    // Check if we should replace the existing property
    if (existingProperty && !this.isPropertyNewer(property, existingProperty)) {
      // The existing property is newer or equal, don't replace
      return;
    }
    
    // Store the property (either new or newer than existing)
    this.propertyMap.set(key, {
      ...property,
      addedToMemoryAt: Date.now()
    });
    
    // Update object metadata
    this.updateObjectMetadata(property);
    
    // Add to pending persistence if it's from remote or user
    if (property.source === 'remote' || property.source === 'user') {
      this.addToPendingPersistence(property);
    }
    
    // Handle immediate tombstone cleanup
    if (property.propertyName === 'object.tombstoned' && property.value === true) {
      this.cleanupTombstonedObject(property.objectId);
    }
  }

  /**
   * Get all properties for a specific object
   */
  getObjectProperties(objectId: string): Property[] {
    const metadata = this.objectMap.get(objectId);
    if (!metadata) return [];
    
    const properties: Property[] = [];
    for (const propertyName of Array.from(metadata.propertyNames)) {
      const key = `${objectId}:${propertyName}`;
      const property = this.propertyMap.get(key);
      if (property) {
        properties.push(property);
      }
    }
    
    return properties;
  }

  /**
   * Get a specific property for an object
   */
  getObjectProperty(objectId: string, propertyName: string): Property | null {
    const key = `${objectId}:${propertyName}`;
    return this.propertyMap.get(key) || null;
  }

  /**
   * Check if an object exists in memory
   */
  hasObject(objectId: string): boolean {
    return this.objectMap.has(objectId);
  }

  /**
   * Get object metadata including hashes
   */
  getObjectMetadata(objectId: string): ObjectMetadata | null {
    return this.objectMap.get(objectId) || null;
  }

  /**
   * Get all properties that need to be persisted to disk (deduplicated)
   */
  getPropertiesPendingPersistence(): Property[] {
    const allPending: Property[] = [];
    for (const batch of this.pendingPersistence) {
      allPending.push(...batch.properties);
    }
    
    // Deduplicate by objectId:propertyName, keeping the latest version
    const deduplicatedMap = new Map<string, Property>();
    for (const property of allPending) {
      const key = `${property.objectId}:${property.propertyName}`;
      const existing = deduplicatedMap.get(key);
      
      // Keep the property with the latest addedToMemoryAt timestamp
      if (!existing || property.addedToMemoryAt > existing.addedToMemoryAt) {
        deduplicatedMap.set(key, property);
      }
    }
    
    return Array.from(deduplicatedMap.values());
  }

  /**
   * Mark properties as persisted (remove from pending queue)
   */
  markPropertiesAsPersisted(): void {
    this.pendingPersistence = [];
  }

  /**
   * Get properties added since a specific timestamp
   */
  getPropertiesSince(timestamp: number): Property[] {
    const properties: Property[] = [];
    for (const property of Array.from(this.propertyMap.values())) {
      if (property.addedToMemoryAt > timestamp) {
        properties.push(property);
      }
    }
    return properties;
  }

  // Private helper methods

  private makePropertyKey(property: Property): string {
    // Use object_id:property_name as the key to ensure one property per object
    // LWW conflict resolution happens in addProperty() before storing
    return `${property.objectId}:${property.propertyName}`;
  }

  private isPropertyNewer(prop1: Property, prop2: Property): boolean {
    // For special properties (object.*), use logical timestamp for comparison
    if (prop1.propertyName.startsWith('object.')) {
      return prop1.logicalTimestamp > prop2.logicalTimestamp;
    }
    
    // For regular properties, use LWW comparison
    if (prop1.lwwCounter !== prop2.lwwCounter) {
      return (prop1.lwwCounter || 0) > (prop2.lwwCounter || 0);
    }
    return (prop1.lwwRandom || 0) > (prop2.lwwRandom || 0);
  }

  private updateObjectMetadata(property: Property): void {
    let metadata = this.objectMap.get(property.objectId);
    
    if (!metadata) {
      // Only allow creating new objects via object.metadata property
      if (property.propertyName !== 'object.metadata') {
        throw new Error(`Cannot add property '${property.propertyName}' to object '${property.objectId}': object.metadata must be added first`);
      }
      
      // Parse metadata value to get type and parent
      const metadataValue = typeof property.value === 'string' 
        ? JSON.parse(property.value) 
        : property.value;
      
      metadata = {
        objectId: property.objectId,
        objectType: metadataValue.type || 'unknown',
        parentId: metadataValue.parent_id || null,
        propertyNames: new Set(),
      };
      this.objectMap.set(property.objectId, metadata);
    }
    
    // Add property name to object's property set
    metadata.propertyNames.add(property.propertyName);
    
    // Update object type and parent if this is metadata
    if (property.propertyName === 'object.metadata') {
      const metadataValue = typeof property.value === 'string' 
        ? JSON.parse(property.value) 
        : property.value;
      metadata.objectType = metadataValue.type || metadataValue.object_type || metadata.objectType;
      metadata.parentId = metadataValue.parent_id || metadata.parentId;
    }
  }

  private addToPendingPersistence(property: Property): void {
    // Add to the most recent batch, or create a new batch
    if (this.pendingPersistence.length === 0) {
      this.pendingPersistence.push({
        properties: [property],
        addedAt: Date.now()
      });
    } else {
      const lastBatch = this.pendingPersistence[this.pendingPersistence.length - 1];
      lastBatch.properties.push(property);
    }
  }

  /**
   * Immediately clean up all properties of a tombstoned object (except the tombstone property itself)
   * This prevents memory leaks by removing deleted objects from memory
   */
  private cleanupTombstonedObject(objectId: string): void {
    const metadata = this.objectMap.get(objectId);
    if (!metadata) return;

    // Remove all properties except the tombstone property
    for (const propertyName of Array.from(metadata.propertyNames)) {
      if (propertyName !== 'object.tombstoned') {
        const key = `${objectId}:${propertyName}`;
        this.propertyMap.delete(key);
        metadata.propertyNames.delete(propertyName);
      }
    }

    // Clean up from pending persistence (remove non-tombstone properties)
    for (const batch of this.pendingPersistence) {
      batch.properties = batch.properties.filter(prop => 
        prop.objectId !== objectId || prop.propertyName === 'object.tombstoned'
      );
    }

    // Remove empty batches
    this.pendingPersistence = this.pendingPersistence.filter(batch => 
      batch.properties.length > 0
    );

  }


  /**
   * Compute content hash for an object based on its properties using shared algorithm
   */
  computeContentHash(objectId: string): string {
    const properties = this.getObjectProperties(objectId);
    
    // Get the latest value for each property name (LWW resolution)
    const latestProperties: Record<string, any> = {};
    for (const property of properties) {
      const current = latestProperties[property.propertyName];
      if (!current || this.isPropertyNewer(property, current)) {
        latestProperties[property.propertyName] = property;
      }
    }
    
    // Convert to format expected by shared hash function
    const propertiesForHashing: PropertyForHashing[] = Object.entries(latestProperties)
      .map(([name, prop]) => ({
        property: name,
        value: typeof prop.value === 'string' ? prop.value : JSON.stringify(prop.value)
      }));
    
    return computeContentHash(propertiesForHashing);
  }

  /**
   * Compute subtree hash for an object based ONLY on children's subtree hashes
   * Content hash and subtree hash are independent - subtree hash should not include content
   */
  computeSubtreeHash(objectId: string): string {
    // Get all child objects currently in memory
    const children = this.getChildObjects(objectId);
    
    // If no children, subtree hash should be "0" (consistent with backend)
    // This ensures objects without loaded children are marked as incomplete subtrees
    if (children.length === 0) {
      return "0";
    }
    
    // Collect children's subtree hashes
    const childSubtreeHashes: string[] = [];
    for (const childId of children) {
      const childSubtreeHash = this.computeSubtreeHash(childId);
      childSubtreeHashes.push(childSubtreeHash);
    }
    
    // Compute hash based ONLY on children (no content hash included)
    const sortedChildHashes = childSubtreeHashes.sort();
    const subtreeData = sortedChildHashes.join('|');
    
    // Use simple hash function directly (independent of content)
    let hash = 0;
    for (let i = 0; i < subtreeData.length; i++) {
      const char = subtreeData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }

  /**
   * Update version vector for an object
   */
  updateVersionVector(objectId: string): void {
    const metadata = this.objectMap.get(objectId);
    if (!metadata) return;
    
    const properties = this.getObjectProperties(objectId);
    const versionVector: Record<string, number> = {};
    
    // Find highest timestamp for each replica
    for (const property of properties) {
      const replicaId = property.replicaId.toString();
      if (!versionVector[replicaId] || property.logicalTimestamp > versionVector[replicaId]) {
        versionVector[replicaId] = property.logicalTimestamp;
      }
    }
    
    metadata.versionVector = versionVector;
  }

  /**
   * Recompute all hashes and version vectors for all objects
   */
  recomputeHashes(): void {
    // First update all version vectors
    for (const objectId of Array.from(this.objectMap.keys())) {
      this.updateVersionVector(objectId);
    }
    
    // Then compute hashes (content first, then subtree in dependency order)
    const processedObjects = new Set<string>();
    
    for (const objectId of Array.from(this.objectMap.keys())) {
      this.computeHashesRecursively(objectId, processedObjects);
    }
  }

  /**
   * Get all child objects of a given object
   */
  getChildObjects(parentId: string): string[] {
    const children: string[] = [];
    
    for (const [objectId, metadata] of Array.from(this.objectMap)) {
      if (metadata.parentId === parentId) {
        children.push(objectId);
      }
    }
    
    return children.sort(); // Sort for consistent ordering
  }

  /**
   * Get total number of objects in memory
   */
  getObjectCount(): number {
    return this.objectMap.size;
  }

  /**
   * Get total number of properties in memory
   */
  getPropertyCount(): number {
    return this.propertyMap.size;
  }

  /**
   * Get all objects in the document
   */
  getAllObjects(): ObjectMetadata[] {
    return Array.from(this.objectMap.values());
  }

  /**
   * Get version vector for an object
   */
  getVersionVector(objectId: string): Record<string, number> {
    const metadata = this.objectMap.get(objectId);
    return metadata?.versionVector || {};
  }

  // Private helper methods

  private computeHashesRecursively(objectId: string, processedObjects: Set<string>): void {
    if (processedObjects.has(objectId)) return;
    
    const metadata = this.objectMap.get(objectId);
    if (!metadata) return;
    
    // First process all children
    const children = this.getChildObjects(objectId);
    for (const childId of children) {
      this.computeHashesRecursively(childId, processedObjects);
    }
    
    // Then compute hashes for this object
    metadata.contentHash = this.computeContentHash(objectId);
    metadata.subtreeHash = this.computeSubtreeHash(objectId);
    metadata.lastHashComputation = Date.now();
    
    processedObjects.add(objectId);
  }

  private simpleHash(input: string): string {
    // Simple hash function for testing - in production, use crypto.subtle or similar
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }
}
